import {
  Input,
  Select,
  Upload,
  Button,
  Space,
  Radio,
  Checkbox,
  message,
  Table,
} from "antd";
import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  UploadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import React, { useEffect, useState, useRef } from "react";
import type { UploadFile, UploadProps } from "antd";
import Papa from "papaparse";
import axiosInstance from "../../../../utils/axiosInstance";
import {
  VARIABLE_SUMMARY_API,
  SEASONALITY_API,
  SIMILARITY_MATRIX_API,
  CORRELATION_MATRIX_API,
  VARIATION_INFLATION_FACTOR_API,
} from "../../../../constants/API";
import { TableRecord } from "../../../../components/Table";
import { DataStructure, UploadDataProps } from "../../../../models/interfaces";
type VariableItem = {
  id: string;
  label: string;
  checked: boolean;
};

type VariableCategory = {
  title: string;
  subtitle: string;
  items: VariableItem[];
};

type VariableKey = "spendVariable" | "impressionVariable" | "nonMediaVariable";

type VariablesData = Record<VariableKey, VariableCategory>;
interface VariableSorterProps {
  data: VariablesData;
  setData: React.Dispatch<React.SetStateAction<VariablesData>>;
  handleCheckboxChange: (key: VariableKey, id: string) => void;
}
const formatNumber = (value: string | number) => {
  if (typeof value === "string" && !isNaN(Number(value))) {
    const num = Number(value);
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }
  return value;
};

const UploadData: React.FC<UploadDataProps> = ({
  setFileUploaded,
  initialValues,
  onValuesChange,
  setVariableSummaryStatistic,
  setHeatMapData,
  setMultiColinearityRecords,
  setCurrent,
  current,
  onDataUpload,
  setAllVariiables,
  setName,
  setFile,
  trigger,
  setTrigger,
  setLoading,
  name,
  file,
  setIsBusiness,
  IsBusiness,
  setDataType,
  responseVariable,
  setResponseVariable,
  seasonLabel,
  seasonDataSet,
  setSeasonLabel,
  setSeasonDataSet,
  dataType,
  isEditMode = false,
  isViewOnly = false,
  loading = false,
  variable,
}) => {
  const [data, setData] = useState<DataStructure>({
    spendVariable: {
      title: "Spend Variable",
      subtitle: "Marketing Spends",
      items: [],
    },
    impressionVariable: {
      title: "Impression Variable",
      subtitle: "KPIs to track",
      items: [],
    },
    nonMediaVariable: {
      title: "Non-Media Variable",
      subtitle: "Additional Variables",
      items: [],
    },
  });

  const [dateColumnName, setDateColumnName] = useState<string>("Date");
  const [csvData, setCsvData] = useState<any[]>([]);
  const [columns, setColumns] = useState<
    {
      title: string;
      dataIndex: string;
      ellipsis?: boolean;
      width?: number;
      render?: (text: string) => JSX.Element;
    }[]
  >([]);
  const [fileList, setFileList] = useState<UploadFile[] | any>(
    file ? [file] : []
  );

  const submitImportForm = async (formData: any) => {
    try {
      setLoading(true);
      const file = fileList[0]; // This is a Blob (File)
      setFile(file);
      const similarityFormData = new FormData();
      if (file) {
        similarityFormData.append("csv_file", file); // Type-safe append
      }
      similarityFormData.append("target_variable", formData.responseVariable);
      data.spendVariable.items.forEach((variable: any) => {
        similarityFormData.append("spend_variables", variable.label);
      });
      data.impressionVariable.items.forEach((variable: any) => {
        similarityFormData.append("impression_variables", variable.label);
      });

      data.nonMediaVariable.items.forEach((variable: any) => {
        similarityFormData.append("non_media_variables", variable.label);
      });

      const seasonalityFormData = new FormData();

      if (file) {
        seasonalityFormData.append("csv_file", file); // Type-safe append
      }
      seasonalityFormData.append("date_column", dateColumnName);
      seasonalityFormData.append("response_variable_column", responseVariable);
      seasonalityFormData.append("yearly_seasonality", "true");

      const seasonalityResponse = await axiosInstance.post(
        SEASONALITY_API,
        seasonalityFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );

      const seasonalityData = seasonalityResponse.data.seasonality_data;

      const labels = seasonalityData.map((item: any) => item[dateColumnName]);
      const seasonData = seasonalityData.map((item: any) => item.Season);

      setSeasonLabel(labels);

      setSeasonDataSet([
        {
          name: "Season",
          data: seasonData,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.3)",
        },
      ]);

      const variableSummaryFormData = new FormData();
      if (file) {
        variableSummaryFormData.append("csv_file", file); // Type-safe append
      }
      variableSummaryFormData.append("date_column", dateColumnName);
      variableSummaryFormData.append("date_format", formData.dateFormat);
      data.impressionVariable.items.forEach((variable: any) => {
        variableSummaryFormData.append("columns", variable.label);
      });
      data.nonMediaVariable.items.forEach((variable: any) => {
        variableSummaryFormData.append("columns", variable.label);
      });
      data.spendVariable.items.forEach((variable: any) => {
        variableSummaryFormData.append("columns", variable.label);
      });
      [
        ...formData.spendVariable,
        ...formData.impressionVariable,
        ...formData.nonMediaVariableVariable,
      ].forEach((column: any) => {
        variableSummaryFormData.append("columns", column.value);
      });

      const variableSummaryResponse = await axiosInstance.post(
        VARIABLE_SUMMARY_API,
        variableSummaryFormData
      );
      setVariableSummaryStatistic(
        variableSummaryResponse.data.variable_summary
      );

      const correlationMatrixForm = new FormData();

      if (file) {
        correlationMatrixForm.append("csv_file", file); // Type-safe append
      }
      correlationMatrixForm.append(
        "target_variable",
        data.spendVariable.items[0].label
      );
      data.spendVariable.items.map((item: any) => {
        correlationMatrixForm.append("spend_variables", item.label);
      });
      if (data.nonMediaVariable.items instanceof Array) {
        data.nonMediaVariable.items.map((item: any) => {
          correlationMatrixForm.append("non_media_variables", item.label);
        });
      }
      data.impressionVariable.items.map((item: any) => {
        correlationMatrixForm.append("impression_variables", item.label);
      });

      correlationMatrixForm.append(
        "response_variable_column",
        responseVariable
      );

      correlationMatrixForm.append("target_variable", responseVariable);
      const correlationMatrixResponse = await axiosInstance.post(
        CORRELATION_MATRIX_API,
        correlationMatrixForm,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );

      const correlationMatrix =
        correlationMatrixResponse.data.correlation_matrix;
      const xLabels = Object.keys(correlationMatrix);
      const yLabels = xLabels;
      const zData = xLabels.map((x) =>
        yLabels.map((y) => correlationMatrix[x][y])
      );
      setHeatMapData({
        xLabels,
        yLabels,
        zData,
      });
      const multiColinearityForm = new FormData();
      if (file) {
        multiColinearityForm.append("csv_file", file);
      }
      data.spendVariable.items.map((item: any) => {
        multiColinearityForm.append("spend_variables", item.label);
      });
      if (data.nonMediaVariable.items instanceof Array) {
        data.nonMediaVariable.items.map((item: any) => {
          multiColinearityForm.append("non_media_variables", item.label);
        });
      }
      data.impressionVariable.items.map((item: any) => {
        multiColinearityForm.append("impression_variables", item.label);
      });

      const multiColinearityResponse = await axiosInstance.post(
        VARIATION_INFLATION_FACTOR_API,
        multiColinearityForm,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          },
        }
      );
      if (typeof multiColinearityResponse.data === "string") {
        try {
          multiColinearityResponse.data = JSON.parse(
            multiColinearityResponse.data.replaceAll("Infinity", "1")
          );
        } catch (error) {
          console.error("Failed to parse JSON string:", error);
        }
      }

      const records: TableRecord[] = [];
      Object.keys(multiColinearityResponse.data.variance_inflation_factors).map(
        (key) => {
          records.push({
            variable: key,
            value:
              multiColinearityResponse.data.variance_inflation_factors[key],
          });
        }
      );
      setMultiColinearityRecords(records);
      setLoading(false);
    } catch (error) {
      console.error("Error in submitImportForm:", error);
      setLoading(false);
    } finally {
      setLoading(false);
      setCurrent(current + 1);
    }
  };
  useEffect(() => {
    if (responseVariable === "Select Response Variable" && trigger) {
      message.error("Please select a response variable");
      setTrigger(false);
      return;
    }
    if (trigger) {
      submitImportForm({
        responseVariable: responseVariable, // Example value, replace with actual form data
        dateVariable: "date",
        dateFormat: "%m/%d/%Y",
        spendVariable: [],
        impressionVariable: [],
        nonMediaVariableVariable: [],
      });
      setTrigger(false);
    }
  }, [trigger, isEditMode]);
  // Handle checkbox change with proper typing
  const handleCheckboxChange = (
    category: keyof DataStructure,
    itemId: number
  ) => {
    setData((prevData) => {
      const updatedCategory = { ...prevData[category] };
      updatedCategory.items = updatedCategory.items.map((item) =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      );

      const updatedData = { ...prevData, [category]: updatedCategory };

      // Update parent component's state
      setAllVariiables(updatedData);

      return updatedData;
    });
  };

  // Handle moving items between categories with proper typing
  const moveItems = (
    fromCategory: keyof DataStructure,
    toCategory: keyof DataStructure,
    direction: "left" | "right"
  ) => {
    setData((prevData) => {
      const from = { ...prevData[fromCategory] };
      const to = { ...prevData[toCategory] };

      const itemsToMove = from.items.filter((item) => item.checked);
      if (itemsToMove.length === 0) return prevData;

      from.items = from.items.filter((item) => !item.checked);
      to.items = [
        ...to.items,
        ...itemsToMove.map((item) => ({ ...item, checked: false })),
      ];

      const updatedData = {
        ...prevData,
        [fromCategory]: from,
        [toCategory]: to,
      };

      // Update parent component's state
      setAllVariiables(updatedData);

      return updatedData;
    });
  };
  const processGeneratedColumns = (
    columns: { title: string; dataIndex: string }[]
  ) => {
    // Log columns for debugging
    console.log("Processing columns:", columns.map(c => c.title));

    // Case-insensitive pattern matching for better flexibility
    const isSpendColumn = (title: string) => {
      const lower = title.toLowerCase();
      return lower.includes("spend") || lower.includes("cost") || lower.includes("budget");
    };

    const isImpressionColumn = (title: string) => {
      const lower = title.toLowerCase();
      return lower.includes("impression") || lower.includes("view") || lower.includes("reach");
    };

    const isResponseColumn = (title: string) => {
      const lower = title.toLowerCase();
      return lower.includes("sales") || lower.includes("revenue") || lower.includes("conversion");
    };

    const spendItems = columns
      .filter((col) => isSpendColumn(col.title))
      .map((col, index) => ({
        id: index + 1,
        label: col.title,
        checked: false,
      }));

    const impressionItems = columns
      .filter((col) => isImpressionColumn(col.title))
      .map((col, index) => ({
        id: index + 1 + spendItems.length,
        label: col.title,
        checked: false,
      }));

    const nonMediaItems = columns
      .filter(
        (col) =>
          !isSpendColumn(col.title) &&
          !isImpressionColumn(col.title) &&
          !isResponseColumn(col.title)
      )
      .map((col, index) => ({
        id: index + 1 + spendItems.length + impressionItems.length,
        label: col.title,
        checked: false,
      }));

    // Filter out Date from items (case-insensitive)
    const filteredSpendItems = spendItems.filter((ele) => ele.label.toLowerCase() !== "date");
    const filteredImpressionItems = impressionItems.filter(
      (ele) => ele.label.toLowerCase() !== "date"
    );
    const filteredNonMediaItems = nonMediaItems.filter(
      (ele) => ele.label.toLowerCase() !== "date"
    );

    console.log("Categorized columns:", {
      spend: filteredSpendItems.length,
      impression: filteredImpressionItems.length,
      nonMedia: filteredNonMediaItems.length
    });

    // Create a consistent data structure for both local and parent state
    const updatedData = {
      spendVariable: {
        title: "Spend Variable",
        subtitle: "Marketing Spends",
        items: filteredSpendItems,
      },
      impressionVariable: {
        title: "Impression Variable",
        subtitle: "KPIs to track",
        items: filteredImpressionItems,
      },
      nonMediaVariable: {
        title: "Non-Media Variable",
        subtitle: "Additional Variables",
        items: filteredNonMediaItems,
      },
    };

    // Update both local and parent state with the same data
    setData(updatedData);
    setAllVariiables(updatedData);
  };
  const uploadProps: UploadProps = {
    name: "file",
    accept: ".csv",
    beforeUpload: (file) => {
      setLoading(true);
      Papa.parse(file, {
        complete: (result) => {
          if (result.data.length > 0) {
            const headers = result.data[0] as string[];

            // Detect date column automatically
            const detectedDateColumn = headers.find(h => h.toLowerCase() === 'date') || headers[0];
            setDateColumnName(detectedDateColumn);

            const generatedColumns = headers.map((header) => ({
              title: header,
              dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
              ellipsis: true,
              width: Math.max(header.length * 15, 120),
              render: (text: any) => {
                let displayValue = text;
                if (typeof text === "object" && text !== null) {
                  try {
                    displayValue = JSON.stringify(text);
                  } catch {
                    displayValue = String(text);
                  }
                }
                const formattedValue = formatNumber(displayValue);
                return (
                  <div style={{ whiteSpace: "nowrap" }}>{formattedValue}</div>
                );
              },
            }));
            processGeneratedColumns(generatedColumns); // Process and categorize columns
            const tableData = result.data.slice(1).map((row: any, index) => ({
              key: index,
              ...headers.reduce((acc, header, i) => {
                acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                return acc;
              }, {} as Record<string, any>),
            }));
            setColumns(generatedColumns);
            setCsvData(tableData);
            onDataUpload?.(tableData, generatedColumns); // Pass data and columns to parent
            setFileUploaded?.(true); // Enable the Next button
            message.success(`${file.name} uploaded successfully`);
          } else {
            message.error("Empty CSV or invalid format.");
            setFileUploaded?.(false);
          }
          setLoading(false);
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          setCsvData([]);
          setLoading(false);
          setFileUploaded?.(false);
        },
        skipEmptyLines: true,
      });
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    fileList,
    onRemove: () => {
      setFileList([]);
      setCsvData([]);
      setFileUploaded?.(false); // Disable the Next button on file removal
    },
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: <CloseOutlined className="text-red-500" />,
    },
  };
  console.log(fileList, "fileList");
  useEffect(() => {
    if (fileList.length) {
      const file: any = fileList[0];
      if (file) {
        Papa.parse(file, {
          complete: (result) => {
            if (result.data.length > 0) {
              const headers = result.data[0] as string[];

              // Detect date column automatically
              const detectedDateColumn = headers.find(h => h.toLowerCase() === 'date') || headers[0];
              setDateColumnName(detectedDateColumn);

              const generatedColumns = headers.map((header) => ({
                title: header,
                dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
                ellipsis: true,
                width: Math.max(header.length * 15, 120),
                render: (text: any) => {
                  let displayValue = text;
                  if (typeof text === "object" && text !== null) {
                    try {
                      displayValue = JSON.stringify(text);
                    } catch {
                      displayValue = String(text);
                    }
                  }
                  const formattedValue = formatNumber(displayValue);
                  return (
                    <div style={{ whiteSpace: "nowrap" }}>{formattedValue}</div>
                  );
                },
              }));
              processGeneratedColumns(generatedColumns);
              const tableData = result.data.slice(1).map((row: any, index) => ({
                key: index,
                ...headers.reduce((acc, header, i) => {
                  acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                  return acc;
                }, {} as Record<string, any>),
              }));

              setColumns(generatedColumns);
              setCsvData(tableData);
            }
          },
          error: (error) => {
            console.error("CSV parsing error:", error);
            setCsvData([]);
          },
          skipEmptyLines: true,
        });
      }
    }
  }, []);
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        geoLiftStudyName: name,
        buyType: dataType,
        fileList,
      });
    }
  }, [name, dataType]);
  const [isDragging, setIsDragging] = useState(false);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // Remove the dragging class from body
    document.body.classList.remove("dragging-active");

    // Clear any existing timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }

    // If there's no destination or source is same as destination with same index, do nothing
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    try {
      // Create a deep copy of the current data to avoid mutation issues
      const newData = JSON.parse(JSON.stringify(data));

      const sourceKey = source.droppableId as VariableKey;
      const destKey = destination.droppableId as VariableKey;

      // Safely get the items arrays, ensuring they exist
      const sourceItems = Array.isArray(newData[sourceKey]?.items)
        ? [...newData[sourceKey].items]
        : [];

      const destItems = Array.isArray(newData[destKey]?.items)
        ? [...newData[destKey].items]
        : [];

      // Check if source index is valid
      if (source.index >= 0 && source.index < sourceItems.length) {
        // Get the item being moved
        const [movedItem] = sourceItems.splice(source.index, 1);

        // If it's the same list, insert at new position
        if (sourceKey === destKey) {
          // Ensure destination index is valid
          const validDestIndex = Math.min(
            destination.index,
            sourceItems.length
          );
          sourceItems.splice(validDestIndex, 0, movedItem);

          newData[sourceKey].items = sourceItems;
        } else {
          // Moving between lists
          // Ensure destination index is valid
          const validDestIndex = Math.min(destination.index, destItems.length);
          destItems.splice(validDestIndex, 0, movedItem);

          newData[sourceKey].items = sourceItems;
          newData[destKey].items = destItems;
        }

        // Update both local and parent state in one go
        setData(newData);
        setAllVariiables(newData);
      }
    } catch (error) {
      console.error("Error during drag and drop operation:", error);
      message.error(
        "An error occurred during the drag operation. Please try again."
      );
    }
  };

  const onDragStart = () => {
    // Add a class to the body to optimize performance during drag
    document.body.classList.add("dragging-active");

    // Clear any existing timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  };
  // Clean up on component unmount
  useEffect(() => {
    return () => {
      // Clear any timeouts
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      // Remove any body classes that might have been added
      document.body.classList.remove("dragging-active");
    };
  }, []);

  const renderColumn = (key: VariableKey) => (
    <Droppable droppableId={key} mode="standard" isDropDisabled={false}>
      {(provided, snapshot) => (
        <div
          className={`w-1/3 bg-white rounded-xl shadow-lg p-5 overflow-hidden border-2 ${
            snapshot.isDraggingOver
              ? "border-blue-400 bg-blue-50"
              : "border-transparent"
          }`}
          {...provided.droppableProps}
          ref={provided.innerRef}
          data-is-dragging-over={snapshot.isDraggingOver}
        >
          <div className="mb-3 pb-2 border-b border-gray-100">
            <h4 className="font-bold text-gray-800 text-lg">
              {data[key].title}
            </h4>
            <p className="text-gray-500 text-sm">{data[key].subtitle}</p>
          </div>
          <div className="space-y-2 mt-3 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
            {data[key].items.length === 0 ? (
              <div className="text-center py-4 text-gray-400 italic text-sm">
                Drop items here
              </div>
            ) : (
              data[key].items
                .filter((item) => item && item.label && item.label !== "Sales")
                .map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id.toString()}
                    index={index}
                    isDragDisabled={false}
                  >
                    {(provided, snapshot) => (
                      <label
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`flex items-center py-2 px-3 rounded-lg border drag-item ${
                          item.checked
                            ? "bg-blue-50 border-blue-200"
                            : "border-gray-200"
                        } ${
                          snapshot.isDragging
                            ? "bg-blue-100 shadow-md border-blue-300 dragging"
                            : ""
                        } cursor-grab active:cursor-grabbing`}
                        style={{
                          ...provided.draggableProps.style,
                          maxWidth: "100%",
                          wordBreak: "break-all",
                          userSelect: "none",
                          // High performance drag styles
                          transition: "none",
                          ...(snapshot.isDragging
                            ? {
                                zIndex: 9999,
                                boxShadow: "0 5px 10px rgba(0,0,0,0.15)",
                                opacity: 0.9,
                              }
                            : {}),
                        }}
                      >
                        <div className="flex items-center w-full">
                          <span className="whitespace-normal break-all text-sm mr-2 flex-grow">
                            {item.label}
                          </span>
                          <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 6h16M4 12h16m-7 6h7"
                            ></path>
                          </svg>
                        </div>
                      </label>
                    )}
                  </Draggable>
                ))
            )}
            {provided.placeholder}
          </div>
          <div className="mt-3 pt-2 text-xs text-gray-400 text-center">
            {data[key].items.length} item
            {data[key].items.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </Droppable>
  );
  // Add custom scrollbar and drag styles
  const customStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a1a1a1;
    }
    
    /* High performance drag styles */
    .dragging {
      will-change: transform;
      pointer-events: none !important;
      touch-action: none !important;
      cursor: grabbing !important;
    }
    
    /* Hardware acceleration for all draggable items */
    .drag-item {
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
      -webkit-font-smoothing: subpixel-antialiased;
      will-change: transform, opacity;
    }
    
    /* Optimize the droppable containers */
    [data-rbd-droppable-id] {
      transform: translateZ(0);
      will-change: background-color, border-color;
    }
    
    /* Reduce paint operations during drag */
    body.dragging-active * {
      transition: none !important;
      animation: none !important;
    }
  `;

  return (
    <div>
      <style>{customStyles}</style>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <h3 className="mb-1 font-bold">Name</h3>
          <Input
            type="text"
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Your Test Name"
            value={name ?? ""}
            disabled={isViewOnly}
          />
        </div>
        <div>
          <h3 className="mb-1 font-bold">Select Data Type</h3>
          <Select
            style={{ width: "100%" }}
            placeholder="Select data type"
            value={dataType}
            onChange={(value) => setDataType(value)}
            disabled={isViewOnly}
            options={[
              { value: "sales-model", label: "Sales Model" },
              {
                value: "conversion/lead model",
                label: "Conversion Model",
              },
            ]}
          />
        </div>

        <div>
          <h3 className="font-bold">Upload File</h3>
          {isEditMode ? (
            <div className="mt-4 p-3 bg-gray-100 rounded-md">
              <p className="text-gray-600">
                <InfoCircleOutlined className="mr-2" />
                File upload is disabled in view mode. You cannot change the
                uploaded file.
              </p>
            </div>
          ) : (
            <Upload {...uploadProps} className="w-full">
              <Button
                icon={<UploadOutlined />}
                size="large"
                className="flex items-center mt-4 "
              >
                Upload Your Historic Campaign Data
              </Button>
            </Upload>
          )}
        </div>
        <div>
          <div className="flex justify-between w-full my-2">
            <Radio.Group
              value={IsBusiness}
              onChange={(e) => setIsBusiness(e.target.value)}
              disabled={isViewOnly}
            >
              <Radio
                checked={IsBusiness === "IsBusinessNot"}
                value="IsBusinessNot"
              >
                My Business is not Seasonal
              </Radio>
              <Radio
                checked={IsBusiness === "IsBusinessYes"}
                value="IsBusinessYes"
              >
                My Business is Seasonal
              </Radio>
            </Radio.Group>
          </div>
        </div>
        {csvData && csvData?.length > 0 && (
          <div>
            <Table
              columns={columns}
              dataSource={csvData}
              pagination={false}
              scroll={{ x: "max-content", y: 300 }}
              bordered
              size="middle"
              className="w-full shadow-sm"
              components={{
                header: {
                  cell: (props: any) => (
                    <th
                      {...props}
                      style={{
                        ...props.style,
                        whiteSpace: "nowrap",
                        background: "#f5f5f5",
                        fontWeight: 600,
                      }}
                    />
                  ),
                },
              }}
            />
          </div>
        )}
        {fileList.length > 0 && (
          <div>
            <h3 className="mb-1 font-bold">Response Variable</h3>
            <Select
              style={{ width: "100%" }}
              placeholder="Response Variable"
              value={responseVariable}
              onChange={(value) => setResponseVariable(value)}
              disabled={isViewOnly}
              options={[
                { value: "Sales", label: "Sales" },
                {
                  value: "Conversions",
                  label: "Conversion",
                },
              ].map((item) => ({
                value: item.value,
                label: item.label,
              }))}
            />
          </div>
        )}

        {(data.spendVariable.items.length > 0 ||
          data.impressionVariable.items.length > 0 ||
          data.nonMediaVariable.items.length > 0) && (
          <div className="p-6 bg-gray-50 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-700 mb-5 flex items-center">
              <svg
                className="w-5 h-5 mr-2 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                ></path>
              </svg>
              Verify Categories
              <span className="ml-2 text-sm font-normal text-gray-500">
                (Drag and drop items between categories)
              </span>
            </h3>
            <DragDropContext
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              enableDefaultSensors={true}
            >
              <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="flex justify-between gap-6">
                  {renderColumn("spendVariable")}
                  {renderColumn("impressionVariable")}
                  {renderColumn("nonMediaVariable")}
                </div>
                <div className="mt-6 text-center text-sm text-gray-500">
                  <p>
                    Tip: Drag items between categories to organize your
                    variables correctly
                  </p>
                </div>
              </div>
            </DragDropContext>
          </div>
        )}
      </Space>
    </div>
  );
};

export default UploadData;
